import { useTranslation } from 'react-i18next';
import { Card, CardContent, Box } from '@mui/material';
import classNames from 'classnames';

import { DynamicComponentProps } from '@/types/ModuleComponentProp.type';
import DynamicSVG from '@/components/DynamicSvg';
import { ROUTES } from '@/route/routes.constants';
import { SafeLink } from '@/components/SafeLink';
import { useNavigationContext } from '@/hooks/useNavigationContext';
import styles from './ModuleCard.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/rootReducer';
import { isModuleChecked } from '@/utils/checked-modules.util';
import { useCheckpointChecks } from '@/hooks/masterData';
import { hasNestedCheckpointChecks, isCheckpointComplete } from '@/utils/checkpoint-checks.util';

export const ModuleCard: React.FC<DynamicComponentProps> = (props: DynamicComponentProps) => {
  const { inspection, structure: module, isChecklistInvalid = false } = props;
  const { t: translate } = useTranslation();
  const { isBlocked, onBlockedNavigation } = useNavigationContext();
  const checkedModuleNames = useSelector(
    (state: RootState) => state.openedInspection.inspection?.checkedModules
  );
  const checkpointCheckResults = useSelector(
    (state: RootState) => state.openedInspection.inspection?.checkpointCheckResults
  );
  const { checkpointChecks } = useCheckpointChecks();
  const templateId = inspection?.template?.id;
  const isChecked = hasNestedCheckpointChecks(checkpointChecks ?? [], templateId, module.name)
    ? isCheckpointComplete({
        checks: checkpointChecks ?? [],
        results: checkpointCheckResults,
        templateId,
        checkpointCode: module.name,
      })
    : isModuleChecked(checkedModuleNames, module.name);
  if (!inspection) return null;

  return (
    <SafeLink
      to={`${ROUTES.INSPECTIONS}/${inspection.id}/${module.name}`}
      isBlocked={isBlocked}
      onBlockedNavigation={onBlockedNavigation}
      className={styles.link_no_deco}
    >
      <Card
        className={classNames({
          [styles.moduleCardInvalid]: isChecklistInvalid,
        })}
        sx={{
          padding: '.55rem 0',
          marginBottom: '.84rem',
          boxShadow: 'unset',
          borderRadius: '.28rem',
        }}
      >
        <CardContent
          className={styles.moduleCard}
          sx={{
            padding: '.55rem 2rem .55rem .55rem !important',
            position: 'relative',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 1.67rem 0 1rem',
              borderRight: '.11rem solid #e0e0e0',
            }}
          >
            {!!module.icon && <DynamicSVG iconName={module.icon} />}
          </Box>
          {!!module.header && (
            <span className={styles.moduleHeader}>{translate(module.header)}</span>
          )}
          <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            {isChecked && !!module.checkedModuleIcon && (
              <DynamicSVG iconName={module.checkedModuleIcon} />
            )}
          </Box>
        </CardContent>
      </Card>
    </SafeLink>
  );
};
